import { hasDefinedValue } from '@sgme/fp';
import { isEmpty, sumBy, uniq } from 'lodash-es';
import { createSelector } from 'reselect';
import {
  getSecondaryMarketAstroProductStatusFromTab,
  getSecondaryMarketRealtimeAstroProductDenominationFromTab,
  getSecondaryMarketRealtimeAstroProductIsinFromTab,
} from 'state/secondaryMarket/referential/astroProduct/astroProduct.selectors';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import {
  DUPLICATE_CUSTODIANS_ERROR,
  EXTERNAL_INACTIVE_PRODUCT_ERROR,
  INTERNAL_INACTIVE_PRODUCT_ERROR,
  MANDATORY_FIELDS_WARNING,
  MISSING_CLIENT_ERROR,
  MISSING_ISIN_ERROR,
  MISSING_NOTIONAL_ERROR,
  MISSING_QUANTITY_ERROR,
  MISSING_UNALLOCATED_NOTIONAL_ERROR,
} from 'state/secondaryMarket/shared/form/validationResults';
import { getIsUserInternal } from 'state/selectors/common/user';
import type { AppState } from 'state/state.model';

import type { DealSplitList, SecondaryMarketQuoteRtForm, SecondaryMarketRtQuoteTabContent } from '.';

function selectSecondaryMarketRtForm(state: AppState) {
  return state.secondaryMarket.realtimeQuote.form;
}
function getForm(appState: AppState, tabId: string): SecondaryMarketQuoteRtForm | undefined {
  const state = selectSecondaryMarketRtForm(appState);
  return state[tabId];
}

export function getSecondaryMarketRtFormQuantity(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.quantity;
}

export function getSecondaryMarketRtFormNotional(appState: AppState, tabId: string) {
  const quantity = getSecondaryMarketRtFormQuantity(appState, tabId);
  const denomination = getSecondaryMarketRealtimeAstroProductDenominationFromTab(appState, tabId);
  if (quantity !== undefined && denomination !== undefined) {
    return quantity * denomination;
  }
}

const emptyDealSplitRows: DealSplitList = [];

export function getSecondaryMarketRtFormDealSplitRows(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.dealSplits ?? emptyDealSplitRows;
}

export function getSecondaryMarketRtFormTotalAllocatedNotional(appState: AppState, tabId: string) {
  const dealSplitRows = getSecondaryMarketRtFormDealSplitRows(appState, tabId);
  return sumBy(dealSplitRows.filter(hasDefinedValue('custodianId')), d => d.notional ?? 0);
}

export function getSecondaryMarketRtFormTotalUnallocatedNotional(appState: AppState, tabId: string) {
  const notional = getSecondaryMarketRtFormNotional(appState, tabId);
  if (notional === undefined) {
    return undefined;
  }
  const allocated = getSecondaryMarketRtFormTotalAllocatedNotional(appState, tabId);
  return notional - allocated;
}

export function getSecondaryMarketRtFormHasUpfrontFee(appState: AppState, tabId: string) {
  const tradeSide = getSecondaryMarketRtFormTradeWay(appState, tabId);
  if (tradeSide === 'ClientSell') {
    return false;
  }
  const form = getForm(appState, tabId);
  return form?.upfrontFee;
}

export function getSecondaryMarketRtFormCanEditUpfrontFee(appState: AppState, tabId: string) {
  return getSecondaryMarketRtFormTradeWay(appState, tabId) === 'ClientBuy';
}
export function getSecondaryMarketRtFormTradeWay(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.tradeWay;
}
export function getSecondaryMarketRtFormClient(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return isEmpty(form?.client) ? undefined : form?.client;
}

export function getSecondaryMarketRtFormQuoteId(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.quoteId;
}

const StatusNone = 'NONE';

export function getSecondaryMarketRtFormQuoteStatus(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.quoteStatus ?? StatusNone;
}

export function getSecondaryMarketRtFormErrorMessage(appState: AppState, tabId: string) {
  const form = getForm(appState, tabId);
  return form?.errorMessage;
}

const mandatoryFieldsErrors = ['clientMissing', 'quantityMissing', 'notionalMissing', 'unallocatedNotionalMissing'];

export const makeGetSecondaryMarketRtFormValidationErrors = () =>
  createSelector(
    [
      getSecondaryMarketRtFormClient,
      getSecondaryMarketRtFormQuantity,
      getSecondaryMarketRtFormNotional,
      getSecondaryMarketRtFormTotalUnallocatedNotional,
      getSecondaryMarketRtFormDealSplitRows,
      getSecondaryMarketAstroProductStatusFromTab,
      getSecondaryMarketRealtimeAstroProductIsinFromTab,
      getIsUserInternal,
    ],
    (
      client,
      quantity,
      notional,
      unallocatedNotional,
      dealSplitRows,
      legacyProductStatus,
      isin,
      isInternalUser,
    ): SecondaryMarketFormError<SecondaryMarketQuoteRtForm>[] => {
      const errors: SecondaryMarketFormError<SecondaryMarketQuoteRtForm>[] = [];
      if (isInternalUser && client?.id === undefined) {
        errors.push(MISSING_CLIENT_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      if (quantity === undefined) {
        errors.push(MISSING_QUANTITY_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      if (notional === undefined) {
        errors.push(MISSING_NOTIONAL_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      if (unallocatedNotional !== undefined && Math.abs(unallocatedNotional) > Number.EPSILON) {
        errors.push(MISSING_UNALLOCATED_NOTIONAL_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      const allocatedCustodians = dealSplitRows
        .filter(r => r.notional !== undefined && r.notional > 0)
        .map(r => r.custodianId);

      if (uniq(allocatedCustodians).length < allocatedCustodians.length) {
        errors.push(DUPLICATE_CUSTODIANS_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      dealSplitRows.forEach((r, i) => {
        if (!r.custodianId) {
          errors.push({
            fields: [`custodian-${i}`],
            severity: 'Error',
            translationKey: 'missingCustodian',
            values: {},
          });
        }
      });
      if (errors.some(e => e.translationKey && mandatoryFieldsErrors.includes(e.translationKey))) {
        errors.push(MANDATORY_FIELDS_WARNING as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }

      if (legacyProductStatus === 'NotActive' || legacyProductStatus === 'Missing') {
        errors.push(
          (isInternalUser
            ? INTERNAL_INACTIVE_PRODUCT_ERROR
            : EXTERNAL_INACTIVE_PRODUCT_ERROR) as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>,
        );
      }
      if (isin === undefined) {
        errors.push(MISSING_ISIN_ERROR as SecondaryMarketFormError<SecondaryMarketQuoteRtForm>);
      }
      return errors;
    },
  );

export function getRtQuoteTabContentToPersist(
  state: AppState,
  tabId: string,
): SecondaryMarketRtQuoteTabContent | undefined {
  const form = getForm(state, tabId);
  if (form === undefined) {
    return undefined;
  }
  const { quoteId: _quoteId, quoteStatus: _quoteStatus, ...persistedTabContent } = form;
  return { ...persistedTabContent };
}
